import { formatDate } from "@angular/common";
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Inject, Injectable, LOCALE_ID } from "@angular/core";
import { Observable, throwError } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { InfoSiteRequest } from "../model/info-site-request";

export abstract class InfoSiteService {
    abstract findByDepartmentAndSequence(code: string, sequence: string): Observable<InfoSiteRequest | null>;
    abstract findByRequestUid(uid: string): Observable<InfoSiteRequest | null>;
    abstract verify(uid: string, date: Date | null): Observable<void>;
}

@Injectable()
export class HttpInfoSiteService extends InfoSiteService {
    constructor(private http: HttpClient,
        @Inject(LOCALE_ID) private locale: string
    ) { super(); }

    findByDepartmentAndSequence(department: string, sequence: string): Observable<InfoSiteRequest | null> {
        let url = `${environment.api}/request/${department}/${sequence}`;
        return this.http.get(url).pipe(
            catchError((r) => this.handleErrorResponse(r)),
            map(r => this.toInfoSiteRequest(r))
        );
    }

    findByRequestUid(uid: string): Observable<InfoSiteRequest | null> {
        let url = `${environment.api}/request/${uid}`;
        return this.http.get(url).pipe(
            catchError((r) => this.handleErrorResponse(r)),
            map(r => this.toInfoSiteRequest(r))
        );
    }

    verify(uid: string, date: Date): Observable<void> {
        let url = `${environment.api}/request/verify`;
        let body = { requestUid: uid, applicantBirthDate: this.toFormatterBirthDate(date) };
        return this.http.post(url, body).pipe(
            catchError((r) => this.handleErrorResponse(r)),
            map(r => void 0)
        );
    }

    private handleErrorResponse(response: HttpErrorResponse): Observable<never> {
        if (response.status === 400) {
            return throwError(response.error.code);
        }
        return throwError(response);
    }

    private toInfoSiteRequest(request: any): InfoSiteRequest | null {
        if (request === null) {
            return null;
        }

        let model = new InfoSiteRequest();
        model.uid = request.uid;
        model.sourceUid = request.sourceUid;
        model.receptionDate = new Date(request.receptionDate);
        model.internalStatus.name = request.internalStatus.name;
        model.internalStatus.percent = request.internalStatus.percent;
        model.passportStatus.code = this.toRequestStatus(request.passportStatus.id);
        model.passportStatus.color = request.passportStatus.color;
        model.passportStatus.subscription = request.passportStatus.subscription;
        model.passportStatus.name = request.passportStatus.name;
        model.passportStatus.description = request.passportStatus.description;
        model.clones = request.clones.map((c: any) => this.toInfoSiteRequest(c));
        return model;
    }

    private toRequestStatus(id: number): string {
        switch (id) {
            case 1: return 'INFO_IN_PROGRESS';
            case 2: return 'INFO_PROBLEM';
            case 3: return 'INFO_READY';
            case 4: return 'INFO_SUCCESS';
            case 5: return 'INFO_MAIL';
            case 6: return 'INFO_PENDING_DATA_PROCESSING';
            case 7: return 'INFO_DOCUMENTS_IN_PROCESSING';
            default: return 'UNKNOWN';
        }
    }

    private toFormatterBirthDate(date: Date) {
        return formatDate(date, 'ddMMyyyy', this.locale);
    }
}
